import * as Cesium from 'cesium'
import CesiumTools from './CesiumTools'

export const Payload = {
  data() {
    return {
      payloadPC: null,
      altitude: 600e3,
      increasing: true,
      sva: 0,
      sensorEntity: null,
      earthRadius: 0,
      minAlt: 250e3,
      maxAlt: 50e6,
      rotationQuat: null,
      lastPos: null,
      groundPos: new Cesium.Cartesian3.fromDegrees(0, 0, 0)
    }
  },
  watch: {
    altitudeValue(val) {
      window.setTimeout(() => {
        this.altitude = Math.min(this.maxAlt, parseFloat(val) * 1e3)
      }, 1e3)
    }
  },
  methods: {
    initPayloadPC() {
      this.earthRadius = this.viewer.scene.globe.ellipsoid.radii.x
      this.viewer.scene.globe.depthTestAgainstTerrain = true
    },

    createSensorEntity(params) {
      this.removeSensor()
      if (params.shape === 'TYPE_CIRCULAR') {
        this.sva = params.alongTrackSVA
          ? params.alongTrackSVA
          : params.crossTrackSVA
          ? params.crossTrackSVA
          : 10
        this.sva = CesiumTools.avoidExtremeLatitudes(this.sva, false)
        this.initRotationQuaternion()
        this.addConicSensorUsingSVA()
        let boundingRectangle = Cesium.Rectangle.fromCartesianArray([
          this.sensorEntity.position.getValue(this.viewer.clock.currentTime)
        ])
        Cesium.Camera.DEFAULT_VIEW_RECTANGLE = boundingRectangle
      } else if (params.shape === 'TYPE_RECTANGULAR') {
        const alongTrackSVA = params.alongTrackSVA ? params.alongTrackSVA : 10
        const crossTrackSVA = params.crossTrackSVA ? params.crossTrackSVA : 10
        this.addRectangularSensorUsingSVA(alongTrackSVA, crossTrackSVA)
        let boundingRectangle = Cesium.Rectangle.fromCartesianArray(
          this.sensorEntity.polygon.hierarchy.getValue(
            this.viewer.clock.currentTime
          ).positions
        )
        Cesium.Camera.DEFAULT_VIEW_RECTANGLE = boundingRectangle
      }
      this.viewer.trackedEntity = this.sensorEntity
    },

    initRotationQuaternion() {
      let latitudeQuat = new Cesium.Quaternion()
      let longitudeQuat = new Cesium.Quaternion()
      let rotationQuat = new Cesium.Quaternion()
      Cesium.Quaternion.fromAxisAngle(
        new Cesium.Cartesian3(0, 0, 1),
        Cesium.Math.toRadians(0),
        longitudeQuat
      )
      Cesium.Quaternion.fromAxisAngle(
        new Cesium.Cartesian3(0, 1, 0),
        Cesium.Math.toRadians(90),
        latitudeQuat
      )
      Cesium.Quaternion.multiply(longitudeQuat, latitudeQuat, rotationQuat)
      return rotationQuat
    },

    getPolygonHierarchy(
      alongTrackSVA,
      crossTrackSVA,
      direction = CesiumTools.directions.FRONT,
      fromSVA = true
    ) {
      let currPos = this.getPosition()
      let groundVertices = []
      if (fromSVA) {
        groundVertices = this.getGroundVertices(
          alongTrackSVA,
          crossTrackSVA,
          currPos
        )
      } else {
        groundVertices = this.getGroundVerticesFromSwath(
          alongTrackSVA,
          crossTrackSVA
        )
      }
      switch (direction) {
        case CesiumTools.directions.FRONT:
          return new Cesium.PolygonHierarchy([
            currPos,
            groundVertices[0],
            groundVertices[1],
            currPos
          ])
        case CesiumTools.directions.RIGHT:
          return new Cesium.PolygonHierarchy([
            currPos,
            groundVertices[1],
            groundVertices[2],
            currPos
          ])
        case CesiumTools.directions.BOTTOM:
          return new Cesium.PolygonHierarchy([
            currPos,
            groundVertices[2],
            groundVertices[3],
            currPos
          ])
        case CesiumTools.directions.LEFT:
          return new Cesium.PolygonHierarchy([
            currPos,
            groundVertices[3],
            groundVertices[0],
            currPos
          ])
        default:
          break
      }
    },

    getGroundVertices(altAngle, actAngle, satPos) {
      let vertexDirection = new Cesium.Cartesian3()
      let groundVertices = []
      const alongTrackQuaternion = new Cesium.Quaternion()
      const crossTrackQuaternion = new Cesium.Quaternion()
      const vertexQuaternion = new Cesium.Quaternion()
      const alongTrackAxis = new Cesium.Cartesian3(0, 1, 0)
      const crossTrackAxis = new Cesium.Cartesian3(0, 0, 1)
      const dirSigns = [
        [-1, -1],
        [-1, 1],
        [1, 1],
        [1, -1]
      ]
      let ellipsoid = this.viewer.scene.globe.ellipsoid

      dirSigns.forEach((signsPair) => {
        Cesium.Quaternion.fromAxisAngle(
          alongTrackAxis,
          altAngle * signsPair[0],
          alongTrackQuaternion
        )
        Cesium.Quaternion.fromAxisAngle(
          crossTrackAxis,
          actAngle * signsPair[1],
          crossTrackQuaternion
        )
        Cesium.Quaternion.multiply(
          alongTrackQuaternion,
          crossTrackQuaternion,
          vertexQuaternion
        )
        const tmpMatrix = Cesium.Matrix3.fromQuaternion(vertexQuaternion)
        let cart = new Cesium.Cartesian3()
        Cesium.Cartesian3.negate(satPos, cart)
        Cesium.Matrix3.multiplyByVector(tmpMatrix, cart, vertexDirection)
        const tmpRay = new Cesium.Ray(satPos, vertexDirection)
        let rayEllipsoidTest = Cesium.IntersectionTests.rayEllipsoid(
          tmpRay,
          ellipsoid
        )
        let vertexDistance = 0
        if (rayEllipsoidTest) {
          vertexDistance = rayEllipsoidTest.start
        } else {
          let cartoPos = Cesium.Cartographic.fromCartesian(satPos)
          vertexDistance = cartoPos.height + ellipsoid.radii.x
        }
        groundVertices.push(Cesium.Ray.getPoint(tmpRay, vertexDistance))
      })
      return groundVertices
    },

    getGroundVerticesFromSwath(alongTrackSwath, crossTrackSwath) {
      let groundPosCarto = Cesium.Cartographic.fromCartesian(this.groundPos)
      let NW = Cesium.Cartesian3.fromDegrees(
        groundPosCarto.longitude - crossTrackSwath / (111.32 * 1e3),
        groundPosCarto.latitude + alongTrackSwath / (110.574 * 1e3)
      )
      let NE = Cesium.Cartesian3.fromDegrees(
        groundPosCarto.longitude + crossTrackSwath / (111.32 * 1e3),
        groundPosCarto.latitude + alongTrackSwath / (110.574 * 1e3)
      )
      let SW = Cesium.Cartesian3.fromDegrees(
        groundPosCarto.longitude - crossTrackSwath / (111.32 * 1e3),
        groundPosCarto.latitude - alongTrackSwath / (110.574 * 1e3)
      )
      let SE = Cesium.Cartesian3.fromDegrees(
        groundPosCarto.longitude + crossTrackSwath / (111.32 * 1e3),
        groundPosCarto.latitude - alongTrackSwath / (110.574 * 1e3)
      )
      return [NW, NE, SE, SW]
    },

    getPosition() {
      let pos = Cesium.Cartesian3.fromDegrees(0, 0, this.altitude)
      if (this.lastPos && !Cesium.Cartesian3.equals(this.lastPos, pos)) {
        this.viewer.camera.moveBackward(
          (this.altitude -
            Cesium.Cartographic.fromCartesian(this.lastPos).height) *
            2
        )
      }
      this.lastPos = pos
      return pos
    },

    getConePosition() {
      if (
        this.lastPos &&
        !Cesium.Cartesian3.equals(
          this.lastPos,
          Cesium.Cartesian3.fromDegrees(0, 0, this.altitude)
        )
      ) {
        this.viewer.camera.moveBackward(
          (this.altitude -
            Cesium.Cartographic.fromCartesian(this.lastPos).height) *
            2
        )
      }
      this.lastPos = Cesium.Cartesian3.fromDegrees(0, 0, this.altitude)
      return this.groundPos
    },

    getEllipseRadius() {
      return Math.min(this.getLength() * Math.tan(this.sva), 1e7)
    },

    getLength() {
      return this.altitude * 2
    },

    getRadius() {
      return this.getLength() * Math.tan(this.sva)
    },

    getOrientation() {
      return Cesium.Quaternion.fromRotationMatrix(
        Cesium.Matrix4.multiplyByTranslation(
          Cesium.Transforms.eastNorthUpToFixedFrame(
            Cesium.Cartesian3.fromDegrees(0, 47.03883)
          ),
          new Cesium.Cartesian3(0.0, 0.0, this.earthRadius + this.altitude / 2),
          new Cesium.Matrix4()
        )
      )
    },

    showCone() {
      if (this.viewer.scene.mode === Cesium.SceneMode.SCENE3D) {
        return true
      } else {
        return false
      }
    },

    removeSensor() {
      if (this.sensorEntity) {
        this.viewer.entities.remove(this.sensorEntity)
        this.sensorEntity = null
      }
      this.viewer.entities.removeAll()
    },

    addConicSensorUsingSVA() {
      this.sensorEntity = this.viewer.entities.add(
        new Cesium.Entity({
          position: new Cesium.CallbackProperty(
            () => this.getConePosition(),
            false
          ),
          orientation: this.rotationQuat,
          cylinder: {
            length: new Cesium.CallbackProperty(() => this.getLength(), false),
            topRadius: 10,
            bottomRadius: new Cesium.CallbackProperty(
              () => this.getRadius(),
              false
            ),
            material: Cesium.Color.DARKORANGE.withAlpha(0.5),
            show: new Cesium.CallbackProperty(() => this.showCone(), false)
          },
          ellipse: {
            semiMajorAxis: new Cesium.CallbackProperty(
              () => this.getEllipseRadius(),
              false
            ),
            semiMinorAxis: new Cesium.CallbackProperty(
              () => this.getEllipseRadius(),
              false
            ),
            material: Cesium.Color.DARKORANGE.withAlpha(0.5),
            show: new Cesium.CallbackProperty(() => !this.showCone(), false)
          }
        })
      )
    },

    addConicSensorUsingSwath(swath) {
      this.sensorEntity = this.viewer.entities.add(
        new Cesium.Entity({
          position: new Cesium.CallbackProperty(
            () => this.getPosition(),
            false
          ),
          orientation: this.rotationQuat,
          cylinder: {
            length: new Cesium.CallbackProperty(() => this.getLength(), false),
            topRadius: 10,
            bottomRadius: swath,
            material: Cesium.Color.DARKORANGE.withAlpha(0.4),
            show: new Cesium.CallbackProperty(() => this.showCone(), false)
          },
          ellipse: {
            semiMajorAxis: swath,
            semiMinorAxis: swath,
            material: Cesium.Color.DARKORANGE.withAlpha(0.4),
            show: new Cesium.CallbackProperty(() => !this.showCone(), false)
          }
        })
      )
    },

    addRectangularSensorUsingSVA(alongTrackSVA, crossTrackSVA) {
      Object.keys(CesiumTools.directions).forEach((key) => {
        this.sensorEntity = this.viewer.entities.add(
          new Cesium.Entity({
            position: new Cesium.CallbackProperty(
              () => this.getPosition(),
              false
            ),
            polygon: {
              hierarchy: new Cesium.CallbackProperty(
                () =>
                  this.getPolygonHierarchy(
                    alongTrackSVA,
                    crossTrackSVA,
                    CesiumTools.directions[key]
                  ),
                false
              ),
              perPositionHeight: new Cesium.ConstantProperty(true),
              material: new Cesium.ColorMaterialProperty(
                Cesium.Color.DARKORANGE.withAlpha(0.4)
              ),
              show: new Cesium.CallbackProperty(() => this.showCone(), false)
            },
            polyline: {
              positions: new Cesium.CallbackProperty(() => {
                let posArray = this.getGroundVertices(
                  alongTrackSVA,
                  crossTrackSVA,
                  this.getPosition()
                )
                return posArray.concat(posArray[0])
              }, false),
              clampToGround: false,
              arcType: Cesium.ArcType.NONE,
              width: 1.5,
              material: new Cesium.ColorMaterialProperty(
                Cesium.Color.DARKORANGE
              ),
              show: new Cesium.CallbackProperty(() => !this.showCone(), false)
            }
          })
        )
      })
    },

    addRectangularSensorUsingSwath(alongTrackSwath, crossTrackSwath) {
      Object.keys(CesiumTools.directions).forEach((key) => {
        this.sensorEntity = this.viewer.entities.add(
          new Cesium.Entity({
            position: new Cesium.CallbackProperty(
              () => this.getPosition(),
              false
            ),
            polygon: {
              hierarchy: new Cesium.CallbackProperty(
                () =>
                  this.getPolygonHierarchy(
                    alongTrackSwath,
                    crossTrackSwath,
                    CesiumTools.directions[key],
                    false
                  ),
                false
              ),
              perPositionHeight: new Cesium.ConstantProperty(true),
              material: new Cesium.ColorMaterialProperty(
                Cesium.Color.DARKORANGE.withAlpha(0.4)
              ),
              show: new Cesium.CallbackProperty(() => this.showCone(), false)
            },
            polyline: {
              positions: new Cesium.CallbackProperty(() => {
                let posArray = this.getGroundVerticesFromSwath(
                  alongTrackSwath,
                  crossTrackSwath
                )
                return posArray.concat(posArray[0])
              }, false),
              clampToGround: false,
              arcType: Cesium.ArcType.NONE,
              width: 1.5,
              material: new Cesium.ColorMaterialProperty(
                Cesium.Color.DARKORANGE
              ),
              show: new Cesium.CallbackProperty(() => !this.showCone(), false)
            }
          })
        )
      })
    }
  }
}
